import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { 
  User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc 
} from 'firebase/firestore';
import { FIREBASE_AUTH } from '../Firebase'
import TimeService from '../service/TimeService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../Firebase';

// Define the shape of the auth context
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  agreeToMarketing: boolean | null;
  userPermissions: string[]; // Keep for backward compatibility (global permissions only)
  fullUserPermissions: { permissions: string[]; orgPermissions: string[] } | null; // ADD: Full permissions object
  loginWithEmail: (email: string, password: string) => Promise<string>;
  loginWithGoogle: (redirectUrl?: string) => Promise<string>;
  signup: (email: string, password: string, name: string) => Promise<string>;
  logout: (redirectUrl?: string) => void;
  sendVerificationEmail: () => Promise<{ success: boolean; cooldownRemaining: number }>;
  resetPassword: (email: string) => Promise<string>;
  checkEmailVerification: () => boolean;
  updateMarketingPreference: (value: boolean, userEmail?: string) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasOrgPermission: (orgId: string, role: string) => boolean; // ADD: Organization permission checker
  isAppAdmin: () => boolean; // ADD: App admin checker
  getUserOrganizations: () => string[]; // ADD: Get user's organizations
  // Email verification cooldown
  verificationEmailSent: boolean;
  verificationCooldown: number;
  checkVerificationCooldown: () => number;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  error: null,
  agreeToMarketing: null,
  userPermissions: [], // Keep for backward compatibility
  fullUserPermissions: null, // ADD: Full permissions object
  loginWithEmail: async () => "",
  loginWithGoogle: async () => "",
  signup: async () => "",
  logout: () => {},
  sendVerificationEmail: async () => ({ success: false, cooldownRemaining: 0 }),
  resetPassword: async () => "",
  checkEmailVerification: () => false,
  updateMarketingPreference: async () => {},
  hasPermission: () => false,
  hasOrgPermission: () => false, // ADD
  isAppAdmin: () => false, // ADD
  getUserOrganizations: () => [], // ADD
  // Email verification cooldown defaults
  verificationEmailSent: false,
  verificationCooldown: 0,
  checkVerificationCooldown: () => 0
});

/**
 * Processes Firebase error messages to make them more user-friendly
 */
const processFirebaseError = (error: string): string => {
  try {
    const errorCode = error.split('/')[1].split(')')[0].replaceAll('-', ' ');
    return errorCode.charAt(0).toUpperCase() + errorCode.slice(1);
  } catch (e) {
    return 'An unknown error occurred';
  }
};

// Helper function to store permissions in localStorage
const storePermissionsInLocalStorage = (userEmail: string, permissions: { permissions: string[]; orgPermissions: string[] }) => {
  try {
    const key = `permissions_${userEmail}_permissions`;
    localStorage.setItem(key, JSON.stringify(permissions));
    console.log(`📦 Stored permissions in localStorage for ${userEmail}:`, permissions);
  } catch (error) {
    console.error('Error storing permissions in localStorage:', error);
  }
};

// Create the provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [agreeToMarketing, setAgreeToMarketing] = useState<boolean | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]); // Keep for backward compatibility
  const [fullUserPermissions, setFullUserPermissions] = useState<{ permissions: string[]; orgPermissions: string[] } | null>(null); // ADD
  
  // State for email verification cooldown
  const [verificationEmailSent, setVerificationEmailSent] = useState<boolean>(
    localStorage.getItem('verificationEmailSent') === 'true'
  );
  const [verificationCooldown, setVerificationCooldown] = useState<number>(0);
  
  // Initialize Firestore and Functions
  const db = getFirestore();
  const functions = getFunctions(app, "europe-west2");
  // const functions = getFunctions(app);

  // UPDATE: Enhanced permission helper functions
  const hasPermission = (permission: string): boolean => {
    return userPermissions.includes(permission); // Uses legacy userPermissions for backward compatibility
  };

  // ADD: Check organization permission using fullUserPermissions
  const hasOrgPermission = (orgId: string, role: string): boolean => {
    return fullUserPermissions?.orgPermissions?.includes(`${orgId}_${role}`) || false;
  };

  // ADD: Check if user is APP_ADMIN
  const isAppAdmin = (): boolean => {
    return userPermissions.includes('APP_ADMIN'); // Uses legacy userPermissions for backward compatibility
  };

  // ADD: Get user's organizations
  const getUserOrganizations = (): string[] => {
    if (!fullUserPermissions?.orgPermissions) return [];
    
    const orgIds = new Set<string>();
    
    fullUserPermissions.orgPermissions.forEach(permission => {
      if (permission.includes('_')) {
        const orgId = permission.split('_')[0];
        orgIds.add(orgId);
      }
    });
    
    return Array.from(orgIds);
  };

  // UPDATE: Enhanced function to fetch user permissions from Firestore and store in localStorage
  const fetchUserPermissions = async (userEmail: string): Promise<{
    permissions: string[];
    orgPermissions: string[];
  }> => {
    try {
      console.log('🔍 Fetching permissions from Firestore for:', userEmail);
      const userDocRef = doc(db, 'users', userEmail);
      const userDoc = await getDoc(userDocRef);
      
      let permissionsData = { permissions: [], orgPermissions: [] };
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // This matches exactly what the cloud function returns
        permissionsData = {
          permissions: userData.permissions || [],
          orgPermissions: userData.orgPermissions || []
        };
      }
      
      // Store permissions in localStorage every time we fetch from Firestore
      storePermissionsInLocalStorage(userEmail, permissionsData);
      
      return permissionsData;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      const fallbackPermissions = { permissions: [], orgPermissions: [] };
      
      // Still store the fallback in localStorage
      storePermissionsInLocalStorage(userEmail, fallbackPermissions);
      
      return fallbackPermissions;
    }
  };

  // Modified function to update marketing preferences - accepts optional userEmail parameter
  const updateMarketingPreference = async (value: boolean, userEmail?: string): Promise<void> => {
    // If userEmail is provided, use it. Otherwise check if currentUser exists
    if (!userEmail && !currentUser) return;
    
    try {
      // Call the cloud function
      const setMarketingPermission = httpsCallable(functions, 'setMarketingPermission');
      await setMarketingPermission({ agreeToMarketing: value });
      console.log("DONE")
      // Update local state
      setAgreeToMarketing(value);
    } catch (error) {
      console.error('Error updating marketing preference:', error);
      throw error;
    }
  };

  // Helper function to fetch marketing preferences
  const fetchMarketingPreferences = async (userEmail: string): Promise<boolean | null> => {
    try {
      const userDocRef = doc(db, 'users', userEmail);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.agreeToMarketing ?? false;
      }
      return false;
    } catch (error) {
      console.error('Error fetching marketing preferences:', error);
      return false;
    }
  };

  // Helper function to check if there's a pending marketing preference
  const checkPendingMarketingPreference = (email: string): boolean => {
    try {
      const marketingSignUpsStr = localStorage.getItem('marketingSignUps');
      if (!marketingSignUpsStr) return false;
      
      const marketingSignUps = JSON.parse(marketingSignUpsStr);
      const userPrefs = marketingSignUps[email];
      
      return userPrefs && userPrefs.marketing === true && userPrefs.fulfilled === false;
    } catch (error) {
      console.error('Error checking pending marketing preferences:', error);
      return false;
    }
  };

  // Helper function to mark marketing preference as fulfilled
  const markMarketingPreferenceFulfilled = (email: string): void => {
    try {
      const marketingSignUpsStr = localStorage.getItem('marketingSignUps');
      if (!marketingSignUpsStr) return;
      
      const marketingSignUps = JSON.parse(marketingSignUpsStr);
      
      if (marketingSignUps[email]) {
        marketingSignUps[email].fulfilled = true;
        localStorage.setItem('marketingSignUps', JSON.stringify(marketingSignUps));
        console.log(`Marketing preference marked as fulfilled for ${email}`);
      }
    } catch (error) {
      console.error('Error marking marketing preference as fulfilled:', error);
    }
  };

  // Check if the user's email is verified
  const checkEmailVerification = (): boolean => {
    return currentUser?.emailVerified || false;
  };
  
  // Check verification cooldown from localStorage only
  const checkVerificationCooldown = (): number => {
    if (!currentUser || !currentUser.email) return 0;
    
    const cooldownExpires = localStorage.getItem('verificationCooldownExpires');
    if (!cooldownExpires) return 0;
    
    const expiresAt = parseInt(cooldownExpires, 10);
    const now = new Date().getTime();
    
    if (now < expiresAt) {
      const remainingMs = expiresAt - now;
      const remainingSec = Math.ceil(remainingMs / 1000);
      
      if (remainingSec > 0) {
        // Don't call startCooldownTimer here to avoid infinite loops
        setVerificationCooldown(remainingSec);
        setVerificationEmailSent(true);
        return remainingSec;
      }
    }
    
    // Clear expired cooldown
    localStorage.removeItem('verificationCooldown');
    localStorage.removeItem('verificationCooldownExpires');
    localStorage.removeItem('verificationEmailSent');
    setVerificationEmailSent(false);
    setVerificationCooldown(0);
    
    return 0;
  };
  
  // Start a countdown timer for verification cooldown
  const startCooldownTimer = (seconds: number) => {
    if (seconds <= 0) {
      setVerificationCooldown(0);
      return;
    }
    
    setVerificationCooldown(seconds);
    
    // Update local storage for persistence across refreshes
    localStorage.setItem('verificationCooldown', seconds.toString());
    localStorage.setItem('verificationCooldownExpires', 
      (new Date().getTime() + seconds * 1000).toString());
    
    const timer = setTimeout(() => {
      startCooldownTimer(seconds - 1);
    }, 1000);
    
    // Clean up timer if component unmounts
    return () => clearTimeout(timer);
  };
  
  // Effect to initialize cooldown from localStorage on page load/refresh
  useEffect(() => {
    const initCooldown = () => {
      const cooldownExpires = localStorage.getItem('verificationCooldownExpires');
      
      if (cooldownExpires) {
        const expiresAt = parseInt(cooldownExpires, 10);
        const now = new Date().getTime();
        
        if (expiresAt > now) {
          const remainingMs = expiresAt - now;
          const remainingSec = Math.ceil(remainingMs / 1000);
          
          if (remainingSec > 0) {
            startCooldownTimer(remainingSec);
            setVerificationEmailSent(true);
            localStorage.setItem('verificationEmailSent', 'true');
          } else {
            // Clear expired cooldown
            localStorage.removeItem('verificationCooldown');
            localStorage.removeItem('verificationCooldownExpires');
            localStorage.removeItem('verificationEmailSent');
            setVerificationEmailSent(false);
            setVerificationCooldown(0);
          }
        } else {
          // Clear expired cooldown
          localStorage.removeItem('verificationCooldown');
          localStorage.removeItem('verificationCooldownExpires');
          localStorage.removeItem('verificationEmailSent');
          setVerificationEmailSent(false);
          setVerificationCooldown(0);
        }
      }
    };
    
    initCooldown();
  }, []);

  // Effect to check cooldown when user is set
  useEffect(() => {
    if (currentUser && currentUser.email && !currentUser.emailVerified) {
      // Just check the cooldown, don't reset it
      const remaining = checkVerificationCooldown();
      if (remaining > 0) {
        startCooldownTimer(remaining);
      }
    }
  }, [currentUser]);

  // Get current server time or fall back to Date.now() if not available
  const getCurrentTime = async (): Promise<number> => {
    try {
      const serverTime = await TimeService.getServerTime();
      return serverTime ? serverTime.getTime() : Date.now();
    } catch (error) {
      console.error("Error getting server time:", error);
      return Date.now(); // Fallback to local time
    }
  };

  // Enhanced onAuthStateChanged listener with forced reload
  useEffect(() => {
    // First, unsubscribe from any existing listener
    let unsubscribe = () => {};
    
    const setupAuthListener = () => {
      // Set up new auth state listener
      unsubscribe = onAuthStateChanged(FIREBASE_AUTH, async (user) => {
        console.log("Auth state changed:", user ? `User: ${user.email}` : "No user");
        
        // If we have a user, reload the user to get the latest email verification status
        if (user) {
          try {
            // Force reload user to get fresh verification status
            await user.reload();
            // Now set the refreshed user
            setCurrentUser(FIREBASE_AUTH.currentUser);
            
            // Fetch user permissions and marketing preferences
            if (user.email) {
              // Fetch permissions from Firestore and store in localStorage (this happens once on login/refresh)
              const permissionsData = await fetchUserPermissions(user.email);
              
              // Keep backward compatibility: userPermissions contains only global permissions
              setUserPermissions(permissionsData.permissions);
              
              // New fullUserPermissions contains the complete structure
              setFullUserPermissions(permissionsData);
              
              console.log('📋 Loaded permissions from Firestore and stored in localStorage:', {
                globalPermissions: permissionsData.permissions,
                orgPermissions: permissionsData.orgPermissions,
                organizations: permissionsData.orgPermissions.map(p => p.split('_')[0]).filter((v, i, a) => a.indexOf(v) === i)
              });
              
              // First, check if there's a pending preference in localStorage
              const pendingPref = checkPendingMarketingPreference(user.email);
              
              if (pendingPref && user.emailVerified) {
                // If verified user has pending preference, apply it
                // Pass the email explicitly since currentUser might not be set yet
                console.log("updating marketing preference")
                await updateMarketingPreference(true, user.email);
                console.log("setting local")
                // Mark as fulfilled in localStorage
                markMarketingPreferenceFulfilled(user.email);
              } else {
                // Otherwise just get current preference from Firestore
                const marketingPref = await fetchMarketingPreferences(user.email);
                setAgreeToMarketing(marketingPref);
              }
            }
          } catch (error) {
            console.error("Error reloading user:", error);
            // If we can't reload, just use the current user
            setCurrentUser(user);
          }
        } else {
          setCurrentUser(null);
          setAgreeToMarketing(null);
          setUserPermissions([]); // Keep backward compatibility
          setFullUserPermissions(null);
        }
        
        setAuthInitialized(true);
        setLoading(false);
      });
    };
    
    // Set up the listener
    setupAuthListener();
    
    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Login with email and password
  const loginWithEmail = async (email: string, password: string): Promise<string> => {
    try {
      setError(null);
      
      // Reset the time offset before login to ensure fresh calculation
      TimeService.resetTimeOffset();
      
      const result = await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
      
      // Force reload the user to get the latest verification status
      await result.user.reload();
      
      if (!result.user.emailVerified) {
        return "NOT_VERIFIED";
      }
      
      // Fetch permissions after successful login and store in localStorage (happens once)
      if (result.user.email) {
        const permissionsData = await fetchUserPermissions(result.user.email);
        setUserPermissions(permissionsData.permissions);
        setFullUserPermissions(permissionsData);
      }
      
      return "SUCCESS";
    } catch (err: any) {
      const errorMessage = processFirebaseError(err.message);
      setError(errorMessage);
      return `ERROR: ${errorMessage}`;
    }
  };

  // Login with Google
  const loginWithGoogle = async (redirectUrl: string = ""): Promise<string> => {
    try {
      setError(null);
      
      // Reset the time offset before login to ensure fresh calculation
      TimeService.resetTimeOffset();
      
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(FIREBASE_AUTH, provider);
      
      // Force reload the user to get the latest verification status
      await result.user.reload();
      
      // Fetch permissions after successful login and store in localStorage (happens once)
      if (result.user.email) {
        const permissionsData = await fetchUserPermissions(result.user.email);
        setUserPermissions(permissionsData.permissions);
        setFullUserPermissions(permissionsData);
      }
      
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      
      return "SUCCESS";
    } catch (err: any) {
      const errorMessage = processFirebaseError(err.message);
      setError(errorMessage);
      return `ERROR: ${errorMessage}`;
    }
  };

  // Sign up with email
  const signup = async (email: string, password: string, name: string): Promise<string> => {
    try {
      setError(null);
      
      // Reset the time offset before signup to ensure fresh calculation
      TimeService.resetTimeOffset();
      
      const result = await createUserWithEmailAndPassword(FIREBASE_AUTH, email, password);
      
      // Update profile with name
      await updateProfile(result.user, {
        displayName: name
      });
      
      // Create a user document in Firestore
      const userEmail = result.user.email;
      if (userEmail) {
        const userDocRef = doc(db, 'users', userEmail);
        const currentTime = await getCurrentTime();
        // No need to create the document if we're not using Firestore for cooldown
      }
      
      // Send verification email
      const emailResult = await sendVerificationEmail();
      
      // Set default permissions for new user and store in localStorage
      const defaultPermissions = { permissions: [], orgPermissions: [] };
      setUserPermissions([]); // New users typically have no permissions
      setFullUserPermissions(defaultPermissions);
      
      // Store default permissions in localStorage
      if (result.user.email) {
        storePermissionsInLocalStorage(result.user.email, defaultPermissions);
      }
      
      return "SUCCESS";
    } catch (err: any) {
      const errorMessage = processFirebaseError(err.message);
      setError(errorMessage);
      return `ERROR: ${errorMessage}`;
    }
  };

  // Logout
  const logout = (redirectUrl?: string) => {
    signOut(FIREBASE_AUTH).then(() => {
      setAgreeToMarketing(null); // Clear marketing preferences
      setUserPermissions([]); // Clear permissions
      setFullUserPermissions(null);
      
      // Clear verification states
      localStorage.removeItem('verificationCooldown');
      localStorage.removeItem('verificationCooldownExpires');
      localStorage.removeItem('verificationEmailSent');
      localStorage.removeItem('loginView');
      
      // Clear permissions from localStorage on logout
      if (currentUser?.email) {
        const permissionsKey = `permissions_${currentUser.email}_permissions`;
        localStorage.removeItem(permissionsKey);
        console.log(`🗑️ Cleared permissions from localStorage for ${currentUser.email}`);
      }
      
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    });
  };

  // Send verification email with cooldown
  const sendVerificationEmail = async (): Promise<{ success: boolean; cooldownRemaining: number }> => {
    if (!currentUser || !currentUser.email) {
      return { success: false, cooldownRemaining: 0 };
    }
    
    // Check if user is already verified
    if (currentUser.emailVerified) {
      return { success: true, cooldownRemaining: 0 };
    }
    
    // Check cooldown first - directly from localStorage
    const remainingCooldown = checkVerificationCooldown();
    if (remainingCooldown > 0) {
      return { success: false, cooldownRemaining: remainingCooldown };
    }
    
    try {
      // Setup action URL to redirect back to your app after verification
      const actionCodeSettings = {
        url: window.location.origin + '/login',
        handleCodeInApp: true
      };
      
      // Send verification email
      await sendEmailVerification(currentUser, actionCodeSettings);
      
      // Set cooldown in localStorage instead of Firestore
      const now = new Date().getTime();
      localStorage.setItem('verificationEmailSent', 'true');
      localStorage.setItem('verificationLastSent', now.toString());
      localStorage.setItem('verificationCooldownExpires', (now + 60000).toString()); // 60 seconds
      
      // Start the cooldown timer
      startCooldownTimer(60); // 60 seconds cooldown
      
      // Update state
      setVerificationEmailSent(true);
      
      return { success: true, cooldownRemaining: 60 };
    } catch (error) {
      console.error("Error sending verification email:", error);
      return { success: false, cooldownRemaining: 0 };
    }
  };

  // Reset password
  const resetPassword = async (email: string): Promise<string> => {
    try {
      await sendPasswordResetEmail(FIREBASE_AUTH, email);
      return "SUCCESS";
    } catch (err: any) {
      const errorMessage = processFirebaseError(err.message);
      return `ERROR: ${errorMessage}`;
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    agreeToMarketing,
    userPermissions, // Keep for backward compatibility (global permissions only)
    fullUserPermissions, // ADD: Complete permissions object
    loginWithEmail,
    loginWithGoogle,
    signup,
    logout,
    sendVerificationEmail,
    resetPassword,
    checkEmailVerification,
    updateMarketingPreference,
    hasPermission,
    hasOrgPermission, // ADD
    isAppAdmin, // ADD
    getUserOrganizations, // ADD
    // Email verification cooldown
    verificationEmailSent,
    verificationCooldown,
    checkVerificationCooldown
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};